import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Spinner, Alert, Button, Card, CardBody, Input } from 'reactstrap';
import { ArrowLeft, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import usePermission from '../../Hooks/usePermission';
import { COST_REPORT_VIEW } from '../../constants/permissions';
import { fetchMaintenanceSpendByAsset } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { formatCurrency as formatCurrencyBase } from '../../helpers/formatCurrency';
import AssetSpendCard from './AssetSpendCard';

const DEFAULT_PAGE_SIZE = 25;

// Server-supported sort fields. Must stay in sync with
// AssetMaintenanceSpendReportingService.comparator().
const SORT_OPTIONS = [
  { value: 'totalSpend',       labelKey: 'reporting.maintenance_spend_by_asset.card_period_total', fallback: 'Maintenance spend' },
  { value: 'laborCost',        labelKey: 'reporting.cost.summary.labor', fallback: 'Labor' },
  { value: 'partsCost',        labelKey: 'reporting.cost.summary.parts', fallback: 'Parts' },
  { value: 'contractorCost',   labelKey: 'reporting.cost.summary.contractor', fallback: 'Contractor' },
  { value: 'workOrderCount',   labelKey: 'shared.wos', fallback: 'WOs' },
  { value: 'correctivePct',    labelKey: 'reporting.maintenance_spend_by_asset.corrective_pct', fallback: '% Corrective' },
  { value: 'lastWorkOrderAt',  labelKey: 'reporting.maintenance_spend_by_asset.last_wo', fallback: 'Last WO' },
];

const formatMoney = (value, currency) =>
  formatCurrencyBase(value, currency, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * Expanded view of the Maintenance Spend by Asset report — paginated rich-card list, reached
 * from the widget's "See all" link. Routed at /reporting/asset-spend, gated by cost:report:view.
 */
const MaintenanceSpendByAssetTable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission, user } = usePermission();
  const organizationId = user?.activeOrganizationId ?? null;

  const [sortBy, setSortBy] = useState('totalSpend');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Default range: last 90 days.
  const dateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 90);
    return { startDate, endDate };
  }, []);

  const {
    maintenanceSpendByAsset: data,
    maintenanceSpendByAssetLoading: loading,
    maintenanceSpendByAssetError: error,
  } = useSelector((state) => state.Reporting);

  const refresh = useCallback(() => {
    if (!organizationId) return;
    dispatch(fetchMaintenanceSpendByAsset(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      null, null, null, null, null, null,
      pageSize, page * pageSize, sortBy,
    ));
  }, [dispatch, organizationId, dateRange, pageSize, page, sortBy]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!hasPermission(COST_REPORT_VIEW)) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            {t('shared.permission_denied', 'You do not have permission to view this report.')}
          </Alert>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const items = data?.items || [];
  const pagination = data?.pagination;
  const currency = summary?.currency;
  const total = pagination?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Within the current page, the highest spender defines the spend-bar max.
  const maxSpend = items.reduce(
    (max, item) => (item.totalSpend != null && Number(item.totalSpend) > max
      ? Number(item.totalSpend) : max),
    0,
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <Button
                color="link"
                className="p-0 mb-2 text-decoration-none"
                onClick={() => navigate('/reporting')}
              >
                <ArrowLeft size={16} className="me-1" />
                {t('reporting.maintenance_spend_by_asset.back_to_reporting', 'Back to Reporting')}
              </Button>
              <h3 className="mb-1">
                {t('reporting.maintenance_spend_by_asset.title', 'Maintenance Spend by Asset')}
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                {t('reporting.maintenance_spend_by_asset.subtitle', 'Labor + parts + contractor cost per asset over the last 90 days.')}
              </p>
            </div>
          </div>

          {/* Summary band */}
          {summary && (
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <div className="p-3 rounded bg-light">
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('reporting.cost.summary.total_spend', 'Total spend')}
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.3rem' }}>
                    {formatMoney(summary.totalSpend, currency)}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-3 rounded bg-light">
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('reporting.maintenance_spend_by_asset.avg_top_n', 'Avg. maintenance spend')}
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.3rem' }}>
                    {formatMoney(summary.topNAvgSpend, currency)}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-3 rounded bg-light">
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('reporting.maintenance_spend_by_asset.assets_in_scope', 'Assets in scope')}
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.3rem' }}>
                    {summary.assetsInScope ?? 0}
                  </div>
                </div>
              </div>
            </div>
          )}

          {summary && summary.assetsWithMissingPriceData > 0 && (
            <Alert color="warning" className="d-flex align-items-center gap-2 py-2" style={{ fontSize: '0.85rem' }}>
              <AlertTriangle size={16} />
              {t('reporting.maintenance_spend_by_asset.missing_price_summary', '{{count}} asset has parts with missing unit price — totals may be understated.', { count: summary.assetsWithMissingPriceData })}
            </Alert>
          )}

          {/* Toolbar: sort + page size */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '12px' }}>
            <CardBody className="d-flex justify-content-between align-items-center gap-3 flex-wrap py-2">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">
                  {t('reporting.maintenance_spend_by_asset.sort_by', 'Sort by')}
                </span>
                <Input
                  type="select"
                  bsSize="sm"
                  value={sortBy}
                  onChange={(e) => { setPage(0); setSortBy(e.target.value); }}
                  style={{ width: 'auto' }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey, opt.fallback)}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="text-muted small">
                {t('reporting.maintenance_spend_by_asset.showing', 'Showing {{from}}-{{to}} of {{total}}', {
                  from: total === 0 ? 0 : page * pageSize + 1,
                  to: Math.min((page + 1) * pageSize, total),
                  total,
                })}
              </div>
            </CardBody>
          </Card>

          {/* Card list */}
          {loading && (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && error && (
            <Alert color="danger">{error}</Alert>
          )}

          {!loading && !error && items.length === 0 && (
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <CardBody className="text-center text-muted py-5">
                {t('reporting.maintenance_spend_by_asset.empty', 'No maintenance spend recorded for the selected period.')}
              </CardBody>
            </Card>
          )}

          {!loading && !error && items.length > 0 && (
            <div>
              {items.map((asset) => (
                <AssetSpendCard
                  key={asset.assetId}
                  asset={asset}
                  currency={currency}
                  maxSpend={maxSpend}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && !error && items.length > 0 && (
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
              <Input
                type="select"
                bsSize="sm"
                value={pageSize}
                onChange={(e) => { setPage(0); setPageSize(Number(e.target.value)); }}
                style={{ width: 'auto' }}
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </Input>
              <Button
                color="light"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={14} />
              </Button>
              <span style={{ fontSize: '0.85rem' }}>
                {t('reporting.maintenance_spend_by_asset.page_of', '{{page}} of {{total}}', {
                  page: page + 1,
                  total: totalPages,
                })}
              </span>
              <Button
                color="light"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MaintenanceSpendByAssetTable;
